import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { userActions } from 'src/app/models/enums/user-permissions.enum';
import { ManagementApiService } from 'src/app/services';
import { CustomToastService } from 'src/app/services/custom-toast.service';
import { OrganizationDataService } from 'src/app/services/organization-data.service';
import { UserUtilityService } from 'src/app/services/user-utility.service';
import { EditOrganizationSaveModalComponent } from '../../modals/organization/edit-organization-save-modal/edit-organization-save-modal.component';
import { InstanceConfirmationComponent } from '../../modals/organization/instance-confirmation/instance-confirmation.component';
import { InstanceForDashboard } from '../../../models/instance/instance.model';
import { GlobalEmittersService } from 'src/app/services/global-emitters.service';
import { Organization } from '../../../models/organization/organization.model';

@Component({
    selector: 'app-organization-instances',
    templateUrl: './organization-instances.component.html',
    styleUrls: ['./organization-instances.component.scss'],
})
export class OrganizationInstancesComponent implements OnInit, OnDestroy {
    @Input() isFederatedOrg: boolean;
    @Output() navigateBackTab = new EventEmitter<number>();
    destory$ = new Subject();

    instances = [];
    instanceList = [];
    tempInstances = [];
    searchInput = '';
    currentOrganizationName: string;
    orgNameSubscription: Subscription;
    resp: any;
    isFedOrg: boolean;
    hideAddOption: boolean = false;
    instanceLimit: number;
    activeOrganization: Organization;

    constructor(
        private modalService: NgbModal,
        private organizationDataService: OrganizationDataService,
        private router: Router,
        private managementApiService: ManagementApiService,
        private ngxUiLoaderService: NgxUiLoaderService,
        private activatedRoute: ActivatedRoute,
        private user_utility: UserUtilityService,
        private customToastService: CustomToastService,
        private globalEmittersService: GlobalEmittersService
    ) {}

    async ngOnInit(): Promise<void> {
        this.hideAddOption = await this.user_utility.checkUserPermissionDataAndRoute('add-instance', userActions.Create);

        this.handleSubscription();
    }

    handleSubscription() {
        this.organizationDataService.activeOrganizationTab.pipe(takeUntil(this.destory$)).subscribe((resp) => {
            if (resp == 'Instances') {
                if (this.activatedRoute.snapshot.paramMap.get('id')) {
                    this.getInstanceByOrgId(this.activatedRoute.snapshot.paramMap.get('id'));
                }
            }
        });

        this.organizationDataService.currentOrganizationName.pipe(takeUntil(this.destory$)).subscribe((orgName) => {
            this.currentOrganizationName = orgName;
        });

        this.organizationDataService.isFederatedOrganization$.pipe(takeUntil(this.destory$)).subscribe((value) => {
            this.isFedOrg = value;
        });

        this.organizationDataService.instanceLimit.pipe(takeUntil(this.destory$)).subscribe((res) => {
            this.instanceLimit = res;
        });

        this.globalEmittersService.currentOrganization.pipe(takeUntil(this.destory$)).subscribe((res) => {
            if (res) {
                this.activeOrganization = res;
            }
        });

        this.organizationDataService.organizationTabNext.pipe(takeUntil(this.destory$)).subscribe((resp) => {
            if (resp == 'Instances') {
                this.saveClick();
            }
        });

        this.organizationDataService.OrganizationTabBack.pipe(takeUntil(this.destory$)).subscribe((resp) => {
            if (resp == 'Instances') {
                this.navigateBack();
            }
        });
    }

    addInstance() {
        let modalRef = this.modalService.open(InstanceConfirmationComponent, {
            size: 'md',
        });
        modalRef.componentInstance.editMode = this.organizationDataService.isOrganzationEdit;
        modalRef.componentInstance.message =
            "You are about add new organization and continue with creating a new instance with the organization. Please click 'Ok' to confirm";
        modalRef.result
            .then((result) => {
                if (result) {
                    this.router.navigate(['management/add-instance'], {
                        queryParams: { organizationId: this.organizationDataService.organizationData.organisationId },
                    });
                }
            })
            .catch((error) => {
                console.warn(error);
            });
    }

    saveClick() {
        if (this.activatedRoute.snapshot.paramMap.get('id')) {
            let modalRef = this.modalService.open(EditOrganizationSaveModalComponent, {
                size: 'md',
            });
            // TODO: in future this static modal messages can be added in json or in some constants files.
            modalRef.componentInstance.modalMessage = 'You are about to edit the organization. Please click <strong>Ok</strong> to confirm';
            modalRef.result
                .then((result) => {
                    if (result) {
                        this.customToastService.successToast('Organization saved successfully!', 'Success');
                    }
                })
                .catch((error) => {
                    console.warn(error);
                });
        } else {
            let modalRef = this.modalService.open(EditOrganizationSaveModalComponent, {
                size: 'md',
            });
            modalRef.componentInstance.modalMessage = 'You are about to add a new organization. Please click <strong>Ok</strong> to confirm';
            modalRef.result
                .then((result) => {
                    if (result) {
                        this.customToastService.successToast('Organization saved successfully!', 'Success');
                    }
                })
                .catch((error) => {
                    console.warn(error);
                });
        }
    }

    navigateBack() {
        this.organizationDataService.isDisableUsersWizard.next(false);
        this.organizationDataService.isDisableInstanceWizard.next(true);
        if (this.isFedOrg) {
            this.organizationDataService.navigateThroughWizard.next(3);
        } else {
            this.organizationDataService.navigateThroughWizard.next(2);
        }
    }

    async getInstanceByOrgId(instanceId: string) {
        try {
            if (!this.instances.length) {
                this.ngxUiLoaderService.start();
                let instances = await this.managementApiService.getInstanceByOrgId(instanceId).toPromise();

                this.ngxUiLoaderService.stop();
                if (instances.success === true) {
                    this.instances = instances.response;
                    this.instanceList = instances.response;
                    this.tempInstances = this.instances.slice();
                } else {
                    this.user_utility.errorModelPopup(instances.message);
                }
            }
        } catch (e) {
            this.ngxUiLoaderService.stop();
        }
    }

    searchInstances() {
        this.instances = this.tempInstances.filter((item) => {
            return item.name.replace(/\s+/g, '').trim().includes(this.searchInput.replace(/\s+/g, '').trim());
        });
    }

    handleNavigation(instance: InstanceForDashboard) {
        this.globalEmittersService.currentInstanceName.next(instance.name);
        this.globalEmittersService.currentInstanceId.next(instance.instanceId);
        this.globalEmittersService.currentOrgId.next(this.activatedRoute.snapshot.paramMap.get('id'));
        this.globalEmittersService.currentOrg.next(this.currentOrganizationName);
        localStorage.setItem('currentInstanceId', instance.instanceId);
        localStorage.setItem('currentInstanceName', instance.name);
        localStorage.setItem('selectedOrganizationName', this.currentOrganizationName);
        localStorage.setItem('selectedOrgId', this.activatedRoute.snapshot.paramMap.get('id'));
        if (instance?.instanceAssetType?.name) {
            localStorage.setItem('currentAssetType', instance.instanceAssetType.name);
        }
        if (Number(instance.instanceType.index) == 1) {
            this.router.navigate(['/informate/standardflange', false]);
        } else {
            this.router.navigate(['/home']);
        }
    }

    // * helper method for navigation from instance list while creating org
    navigateInstanceEdit(instanceId: string) {
        this.router.navigate(['/management/edit-instance', instanceId], { queryParams: { organizationId: this.activeOrganization.organisationId } });
    }

    ngOnDestroy(): void {
        this.destory$.next(null);
        this.destory$.complete();
    }
}
